import {
  Configuration,
  InvoiceStatusEnum,
  InvoiceTypeEnum,
  InvoicesApi,
  ServiceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import {
  QueryClient,
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import type { RawAxiosRequestConfig } from 'axios';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { ensureStableSort } from '../../utils/helpers/sort';
import { toApiError } from '../../utils/httpError';

export const INVOICES_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 10,
  sortBy: 'beneficiaryWorkspaceName:asc',
} as const satisfies InvoicesQueryParams;

// ===== API CLIENT =====

/**
 * Create an invoices API instance
 */
const getInvoicesApi = () => {
  return new InvoicesApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

// ===== QUERY KEYS =====

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const invoicesKeys = {
  all: ['invoices'] as const,
  paginated: (params: InvoicesQueryParams) =>
    [...invoicesKeys.all, 'paginated', { ...params }] as const,
};

// ===== API FUNCTIONS =====

export interface InvoicesQueryParams {
  /** Added in APP-2180 S3 (Clients Summary Statements section). */
  beneficiaryClientId?: string;
  beneficiaryTitleId?: string;
  beneficiaryWorkspaceId?: string;
  cascadeId?: string;
  invoiceNo?: string;
  invoiceType?: InvoiceTypeEnum;
  ownerTitleId?: string;
  ownerWorkspaceId?: string;
  page?: number;
  pageSize?: number;
  payoutId?: string;
  postedToXeroFrom?: string;
  postedToXeroTo?: string;
  searchTerm?: string;
  serviceId?: string;
  serviceType?: ServiceTypeEnum[];
  sortBy?: string;
  status?: InvoiceStatusEnum;
}

/**
 * The SDK takes these positionally — filters alphabetically, then the
 * pagination params — so a new filter shifts every argument after it.
 */
export const invoiceFindAllArgs = (params: InvoicesQueryParams) =>
  [
    params.beneficiaryClientId,
    params.beneficiaryTitleId,
    params.beneficiaryWorkspaceId,
    params.cascadeId,
    params.invoiceNo,
    params.invoiceType,
    params.ownerTitleId,
    params.ownerWorkspaceId,
    params.payoutId,
    params.postedToXeroFrom,
    params.postedToXeroTo,
    params.searchTerm,
    params.serviceId,
    params.serviceType,
    params.status,
    params.page,
    params.pageSize,
    params.sortBy,
  ] as const;

/**
 * A list of paid invoices, as the Payments tables show it, in a stable order.
 * Their CSV exports build from this too, so a file can't drift from its table.
 */
export const paidInvoicesParams = ({
  sortBy,
  ...params
}: Omit<InvoicesQueryParams, 'status'>) => ({
  ...params,
  sortBy: ensureStableSort(sortBy),
  status: InvoiceStatusEnum.Paid,
});

/**
 * Fetch invoices with optional filters
 */
export const fetchInvoices = async (
  params: InvoicesQueryParams,
  options?: RawAxiosRequestConfig,
) => {
  try {
    const response = await getInvoicesApi().invoiceControllerFindAll(
      ...invoiceFindAllArgs(params),
      options,
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'invoiceControllerFindAll',
      params: params,
    });
  }
};

// ===== QUERY OPTIONS =====

/**
 * React Query options for a paginated invoices list.
 * Skips the fetch when `params` is undefined (caller opt-out).
 */
export const queryOptionsGetInvoicesPaginated = (
  params?: InvoicesQueryParams,
) => {
  const queryParams = {
    ...INVOICES_DEFAULT_PARAMS,
    ...params,
  };

  return queryOptions({
    // React Query is the only cache for this list, so `invalidateQueries`
    // refetches from the server. In the query function, not the fetcher: the
    // fee-invoice checks call the fetcher directly and keep the axios cache.
    queryFn: params
      ? () => fetchInvoices(queryParams, bypassCacheConfig())
      : skipToken,
    queryKey: invoicesKeys.paginated(queryParams),
  });
};

/**
 * Marks every invoice list stale. For writes in other modules that change
 * what those lists return, such as a payout initiate or authorise.
 */
export const invalidateInvoiceQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: invoicesKeys.all });

// ===== QUERY HOOKS =====

/** Narrow opt-in flags for {@link useInvoices} call sites. */
export type UseInvoicesOptions = {
  enabled?: boolean;
  /** Opt in for pagination UX; not set on shared query options (see ActivityStatementsTable). */
  placeholderData?: typeof keepPreviousData;
};

/**
 * Hook for fetching invoices with optional filters.
 * `options` is intentionally narrow so call sites opt into behaviour like
 * `placeholderData: keepPreviousData` without affecting every consumer.
 */
export const useInvoices = (
  params?: InvoicesQueryParams,
  options?: UseInvoicesOptions,
) => {
  return useQuery({
    ...queryOptionsGetInvoicesPaginated(params),
    ...options,
  });
};
